// src/components/dashboard/EmissionsReductionSummary.js
import React, { useMemo } from "react";
import { getSectorColor } from "../../config/colorScheme";
import { useSectorEmissionsWithFood } from "../../hooks/useSectorEmissionsWithFood";

// clamp helper
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
function pctReduction(fromVal, toVal) {
  if (typeof fromVal !== "number" || !isFinite(fromVal) || fromVal === 0) return 0;
  return ((fromVal - toVal) / fromVal) * 100;
}

export default function EmissionsReductionSummary({
  scenario,          // backend scenario key
  from = 2050,       // optional: restrict query/labels
  to = 2050,
  compareYear = 2025 // baseline year
}) {
  // IMPORTANT: pass only EMIS sectors here; FOOD is fetched by the hook's second field
  const { labels, byGroupYear, loading, error } =
    useSectorEmissionsWithFood({ scenario, from, to, emisSectors: ["TRA", "RES", "IND", "FOOD"] });

  // normalise label types to numbers for indexing
  const yearLabels = useMemo(
    () => (labels ?? [])
      .map((y) => (typeof y === "number" ? y : parseInt(y, 10)))
      .filter(Number.isFinite),
    [labels]
  );

  // Build arrays aligned to yearLabels for each of the four sectors
  const seriesBySector = useMemo(() => {
    const get = (code, year) => (byGroupYear?.[code]?.[year] ?? 0);
    return {
      food:       yearLabels.map((y) => get("FOOD", y)),
      buildings:  yearLabels.map((y) => get("BUILDINGS", y)),
      transport:  yearLabels.map((y) => get("TRA", y)),
      industry:   yearLabels.map((y) => get("IND", y)),
    };
  }, [byGroupYear, yearLabels]);

  const {
    totalPct,
    sectorPercents,   // { food, buildings, transport, industry }
    baselineYear,
    targetYear
  } = useMemo(() => {
    if (!yearLabels.length) {
      return { totalPct: 0, sectorPercents: {}, baselineYear: compareYear, targetYear: to };
    }

    const baseIdx = yearLabels.includes(compareYear)
      ? yearLabels.indexOf(compareYear)
      : 0;

    const tgtIdx = yearLabels.includes(to) ? yearLabels.indexOf(to) : yearLabels.length - 1;

    const sectors = ["food", "buildings", "transport", "industry"];

    // sector-level % reduction
    const sectorPercents = sectors.reduce((acc, key) => {
      const series = seriesBySector[key] || [];
      const base = series[baseIdx] ?? null;
      const tgt = series[tgtIdx] ?? null;
      const pct = pctReduction(base, tgt);
      acc[key] = isFinite(pct) ? pct : 0;
      return acc;
    }, {});

    // overall % = sum(baseline) vs sum(target)
    const baseTotal = sectors.reduce((sum, k) => {
      const v = seriesBySector[k]?.[baseIdx];
      return sum + (typeof v === "number" ? v : 0);
    }, 0);

    const tgtTotal = sectors.reduce((sum, k) => {
      const v = seriesBySector[k]?.[tgtIdx];
      return sum + (typeof v === "number" ? v : 0);
    }, 0);

    const totalPct = pctReduction(baseTotal, tgtTotal);

    return {
      totalPct: isFinite(totalPct) ? totalPct : 0,
      sectorPercents,
      baselineYear: yearLabels[baseIdx],
      targetYear: yearLabels[tgtIdx],
    };
  }, [yearLabels, seriesBySector, compareYear, to]);

  if (loading) return <div className="w-full h-40 grid place-items-center">Calculating reductions…</div>;
  if (error) return <div className="w-full h-40 text-red-600">Error: {error.message}</div>;

  // nice render helpers
  const pretty = (n) => `${(Math.round(n * 10) / 10).toFixed(1)}%`;
  const sectorsOrder = ["food", "buildings", "transport", "industry"];
  const labelMap = { food: "Food", buildings: "Buildings", transport: "Transport", industry: "Industry" };

  return (
    <div className="text-center">
      <h2 className="text-xl font-bold">Greenhouse gas emissions reduction</h2>
      <p className="text-sm">
        This represents the total greenhouse gas emission reduction  across all sectors for the selected scenario in 2050 relative to 2025.
      </p>

      <div className="text-3xl font-bold text-green-700 mb-4 bg-slate-200 rounded-lg p-4 m-4">
        <div>{pretty(totalPct)} Reduction</div>
        <span className="block text-xs text-gray-500 mt-2">
          Total greenhouse gas emissions reduction across Food, Buildings, Transport, and Industry
        </span>
      </div>

      {/* Sector breakdown */}
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {sectorsOrder.map((sector) => {
          const pct = clamp(sectorPercents[sector] ?? 0, -100, 100); // allow negative (increase) but clamp
          const color = getSectorColor(sector, "primary");
          const isIncrease = pct < 0;
          const barWidth = Math.abs(pct); // 0..100

          return (
            <div key={sector} className="text-left">
              <div className="flex justify-between items-center text-xs mb-1">
                <div className="flex items-center">
                  <span className="inline-block w-3 h-3 mr-2 rounded-full" style={{ backgroundColor: color }} />
                  <span className="capitalize">{labelMap[sector]}</span>
                </div>
                <span className={`font-semibold ml-4 ${isIncrease ? "text-red-600" : "text-green-700"}`}>
                  {isIncrease ? `+${pretty(-pct)}` : pretty(pct)}
                </span>
              </div>

              {/* mini bar */}
              <div className="w-full h-2 bg-gray-200 rounded">
                <div
                  className="h-2 rounded"
                  style={{
                    width: `${barWidth}%`,
                    backgroundColor: isIncrease ? "#ef4444" : color,
                    opacity: isIncrease ? 0.65 : 1,
                  }}
                  title={`${labelMap[sector]}: ${isIncrease ? "+" : ""}${pretty(pct)}`}
                />
              </div>

              <div className="text-[10px] text-gray-500 mt-1">vs {baselineYear} (baseline)</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
